import React from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';

/**
 * UK Destinations information page
 * Shows customs and shipping information for UK destinations
 */
function DestinationsUK() {
  const [searchParams] = useSearchParams();
  const minBags = searchParams.get('min') || '1';
  const fromCountry = searchParams.get('from') || 'US';

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h2 className="mb-0">
                <i className="fas fa-flag-uk me-2"></i>
                Shipping to the United Kingdom
              </h2>
            </Card.Header>
            <Card.Body>
              {/* Route Information */}
              <Alert variant="info" className="mb-4">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Route:</strong> {fromCountry} → UK | <strong>Minimum:</strong> {minBags} bag(s)
              </Alert>

              {/* Customs Information */}
              <section className="mb-5">
                <h3 className="mb-3">Customs Information</h3>
                <p className="mb-4">
                  Please take a moment to review the important customs information before shipping to the UK.
                </p>

                {/* Personal Effects Customs Clearance UK */}
                <h4 className="mt-4 mb-3">Personal Effects Customs Clearance UK</h4>
                <p>
                  All shipments must enter the UK within 12 months of the consignee's arrival in the UK to be eligible for clearance as used personal effects.
                </p>

                <h5 className="mt-4 mb-3">Temporary Import and Returning UK Residents</h5>
                <p>
                  Shipments of used personal effects that are being returned to the UK after being outside of the UK for less than 12 months such as for a 
                  holiday, and shipments for non-UK residents that will only be in the UK for less than 12 months for reasons other than study before being 
                  exported again, are normally eligible for duty and tax free entry with only a short statement being provided to confirm the reason for import. 
                  This statement can be completed online via your Send My Bag account once an order has been placed. Duty relief is at the discretion of 
                  the customs authority and is not guaranteed.
                </p>
                <p>
                  Shipments for returning UK residents are only eligible for duty free clearance if the goods are declared as of UK origin i.e. are returning goods, if 
                  the contents are declared as mixed origin i.e UK and foreign items, duties and taxes will be levied on the entire shipment and not just the items 
                  of foreign origin.
                </p>

                <h5 className="mt-4 mb-3">Relocating to the UK and Permanent Imports</h5>
                <p>
                  If the shipment is entering the UK permanently for reasons other than study, or is being imported by a returning UK resident who has been 
                  outside the UK for 12 months or more, you will need to apply for a "Transfer of Residence Relief" (ToR) code from UK customs (HMRC) using 
                  form ToR01, in order to obtain duty relief at the time of the shipment's entry.
                </p>
                <p>
                  If you are completing the ToR01 form, we recommend that you tick preview and save as a pdf and the form can then be emailed along with the 
                  requested documentary documents (once the form is completed online the HMRC website will advise what accompanying documents are 
                  required for your particular circumstances) to <a href="mailto:niru.admin.gp@gov.uk">niru.admin.gp@gov.uk</a> rather than being sent by post.
                </p>
                <p>
                  If the consignee is already in possession of a ToR code, this can be supplied to Send My Bag via your account once an order is placed.
                </p>
                <p>
                  If at the time of collection, no ToR code has been supplied, duties, taxes and any other courier fees will be applied and need to be paid by 
                  the recipient. The charges will be billed by our courier partner either prior to delivery or at the time of delivery of the shipment, depending on 
                  the total value of the charge. A claim for rebate of any duty and taxes paid can then be submitted to HMRC by completing a{' '}
                  <a href="https://www.gov.uk/government/publications/import-and-export-application-for-relief-from-customs-duty-bor286" target="_blank" rel="noopener noreferrer">
                    C285 form
                  </a>{' '}
                  or their website. This is the data is required on the C285 form, you will need to contact Send My Bag to supply you with the information and 
                  evidence required. The ToR code is valid for 12 months from the date of issue and can be used for multiple shipments. Please note that under 
                  the £11 - 12 series fee for collection of taxes and any multiline charges applied by the courier. Multiline charges are typically applied by the 
                  courier when more than five different types of items (commodities) are listed on the packing list e.g. clothes would be considered one type of 
                  commodity but clothes and books would count as two types of commodity. Once five commodities are exceeded on the packing list, multiline 
                  fees are charged as a rate of £4.50 per additional commodity.
                </p>

                <h5 className="mt-4 mb-3">Shipping to the UK for Studies</h5>
                <p>
                  If the consignee is travelling to the UK for study, they will be required to provide a copy of their enrolment or placement letter from their 
                  College or University, clearly showing the start and end dates of the course, the course name and the address of the University or College in 
                  order to be eligible for duty and tax free entry. (The letters and letters from UCAS/CAS are not accepted by HMRC for duty free clearance.
                </p>

                <h5 className="mt-4 mb-3">General Personal Effects Customs Information</h5>
                <p>
                  Personal effects are typically defined as used items which have been in the sender's possession for 6 months or more. If your shipment is 
                  checked by customs and is found to contain brand new or commercial items, you shipment may not be assessed as personal effects. Keep this 
                  in mind if you are sending items to sell or other new items. Please check with customs or you are aware of potential customs duties before 
                  sending your shipment.
                </p>
                <p>
                  In order to demonstrate that your shipment consists of personal effects, you will be required to complete a brief non-generic packing list 
                  online, entries to be made in the format: "3 x Cotton Dresses, 2 x Jeans, 3 x Pair of Gloves" additional comments can also be written into the 
                  packing list.
                </p>
                <p>
                  As customs processes and allowances vary by country, we advise all customers take 5 minutes to check how their personal circumstances and 
                  what they are sending will be assessed under the prevailing customs regulations of the destination.
                </p>
                <p>
                  More information is available on our{' '}
                  <a href="/customs-information" target="_blank" rel="noopener noreferrer">customs information page</a>.
                </p>

                <h5 className="mt-4 mb-3">Customs Clearance Time</h5>
                <p>
                  Most shipments on this route will not be stopped by customs, however, when a bag is stopped by customs for any reason (missing 
                  documentation, incorrect inspection, prohibited contents, commercial contents or further information required) Send My Bag records how 
                  long customs hold it for. Our historical statistics for this route indicate that if the customs authority stops your bag they will on average take 1 
                  working day to process it and release it back to us for delivery.
                </p>

                <h5 className="mt-4 mb-3">Shipping Gifts to the UK</h5>
                <p>
                  The United Kingdom allows gifts to enter the country duty free provided they valued at £39 or less (per person). Gifts valued between £39 - 
                  £135 will have VAT at 20% applied and anything declared above £135 will have VAT and a minimum 2.5% duty applied.
                </p>
                <p>
                  To meet the gift criteria, they must be for an 'occasion' such as Birthday, Anniversary or Christmas. They must be sent between 2 individuals i.e 
                  not shipped from or to a business address and the items marked as gifts must be for personal use only.
                </p>
                <p>
                  Where multiple gifts are being sent in the same consignment for multiple people, each item must be individually wrapped and marked for the 
                  different intended recipients. On the packing list, each item listed will need to have a name next to the description of each gift to clearly 
                  mark for customs who the intended recipient is.
                </p>
                <p>
                  Courier admin fees will also apply on top of any duties and taxes billed and are not covered by Send My Bag.
                </p>
              </section>

              {/* Prohibited Items */}
              <section className="mb-5">
                <h3 className="mb-3">Prohibited Items</h3>
                <p className="mb-3">
                  <a href="#" onClick={(e) => { e.preventDefault(); /* Expand logic */ }}>Show More</a>
                </p>
              </section>

              {/* UK Route Alerts */}
              <section className="mb-4">
                <Alert variant="warning" className="border-warning">
                  <h4 className="alert-heading mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    UK Route Alerts
                  </h4>
                  <p className="mb-0">
                    <i className="fas fa-times-circle text-danger me-2"></i>
                    Knives may not be sent on this service.
                  </p>
                </Alert>
              </section>

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default DestinationsUK;
